// ============================================================
// SettingsTab.jsx  —  Noble Enterprises (tape module)
//
// One section: Jumbo.
//
// FORM ORDER
//   Type → Category → Quality → Micron → Width → Meters → Core
//
// WHY MICRON MATTERS
// Waste is weighed in kg but stock is counted in meters. The
// bridge is grams per meter, worked out from the micron's
// weight per square metre and the roll's width:
//     g/m = gsm × width in metres
// Without it, waste never comes off the roll and every jumbo
// reports its full theoretical yield no matter how it was run.
//
// Everything metric. Supplier data is in mm and micron, so
// converting to inches and back only adds rounding error.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. ComboField    — dropdown that can create a new option
//   C. MicronField   — dropdown plus inline "new micron" fields
//   D. JumboForm
//   E. SettingsTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const mm2in = (mm) => (Number(mm) / 25.4).toFixed(1);


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && (
      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>
    )}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const StatusPill = ({ active }) => (
  <span style={{
    fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '11px',
    background: active ? C.successBg : C.borderSoft,
    color:      active ? C.success   : C.textMuted,
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', gap: '9px',
      background: 'none', border: 'none', cursor: 'pointer',
      padding: 0, fontFamily: 'inherit',
    }}
  >
    <span style={{
      width: '38px', height: '22px', borderRadius: '11px',
      background: checked ? C.brand : C.border,
      position: 'relative', transition: 'background 0.15s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: '3px', left: checked ? '19px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#ffffff', transition: 'left 0.15s',
      }} />
    </span>
    <span style={{ fontSize: '14px', color: C.text }}>{label}</span>
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. ComboField — dropdown that can flip to a text box
// ============================================================

const ComboField = ({
  label, options, value, newName, isNew,
  onChange, placeholder, disabled, hint,
}) => (
  <Field label={label} hint={hint}>
    <div style={{ display: 'flex', gap: '8px', opacity: disabled ? 0.5 : 1 }}>
      {isNew ? (
        <TextInput
          autoFocus
          disabled={disabled}
          value={newName}
          onChange={e => onChange({ value: '', newName: e.target.value, isNew: true })}
          placeholder={placeholder}
        />
      ) : (
        <select
          style={inputStyle}
          disabled={disabled}
          value={value}
          onChange={e => onChange({ value: e.target.value, newName: '', isNew: false })}
        >
          <option value="">Select…</option>
          {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange({ value: '', newName: '', isNew: !isNew })}
        style={{
          flexShrink: 0, padding: '0 13px', borderRadius: '8px',
          border: `1px solid ${isNew ? C.brand : C.border}`,
          background: isNew ? C.brand : '#ffffff',
          color: isNew ? '#ffffff' : C.textSoft,
          fontSize: '13px', fontWeight: '600',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}
      >
        {isNew ? 'Cancel' : '+ New'}
      </button>
    </div>
  </Field>
);


// ============================================================
// C. MICRON FIELD
//
// A new micron needs two numbers, not one — the thickness and
// its weight per square metre — so it gets its own component
// rather than reusing ComboField.
// ============================================================

const MicronField = ({ microns, form, setForm }) => {
  const isNew = form.isNewMicron;

  return (
    <Field
      label="Micron"
      hint={isNew ? 'Weight of one square metre of this film' : 'Sets the weight used to convert scrap into meters'}
    >
      {isNew ? (
        <div style={{ display: 'flex', gap: '8px' }}>
          <TextInput
            autoFocus
            type="number" step="0.1"
            value={form.newMicron}
            onChange={e => setForm({ ...form, newMicron: e.target.value })}
            placeholder="µm"
          />
          <TextInput
            type="number" step="0.001"
            value={form.newMicronGsm}
            onChange={e => setForm({ ...form, newMicronGsm: e.target.value })}
            placeholder="g/m²"
          />
          <button
            type="button"
            onClick={() => setForm({ ...form, isNewMicron: false, newMicron: '', newMicronGsm: '' })}
            style={{
              flexShrink: 0, padding: '0 13px', borderRadius: '8px',
              border: `1px solid ${C.brand}`, background: C.brand,
              color: '#ffffff', fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            style={inputStyle}
            value={form.micron_id}
            onChange={e => setForm({ ...form, micron_id: e.target.value })}
          >
            <option value="">Select…</option>
            {microns.map(m => (
              <option key={m.id} value={m.id}>
                {Number(m.micron)} µm — {Number(m.gsm)} g/m²
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setForm({ ...form, isNewMicron: true, micron_id: '' })}
            style={{
              flexShrink: 0, padding: '0 13px', borderRadius: '8px',
              border: `1px solid ${C.border}`, background: '#ffffff',
              color: C.textSoft, fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            + New
          </button>
        </div>
      )}
    </Field>
  );
};


// ============================================================
// D. JUMBO FORM
// ============================================================

const emptyForm = {
  id: null,
  tape_type_id: '',
  category_id: '', newCategory: '', isNewCategory: false,
  quality_id: '',  newQuality: '',  isNewQuality: false,
  micron_id: '',   newMicron: '',   newMicronGsm: '', isNewMicron: false,
  width_mm: '',
  core_width_mm: '',
  default_meters: '',
  core_length_meters: '',
  roll_weight_kg: '',
  is_active: true,
};

const JumboForm = ({
  form, setForm, tapeTypes, categories, qualities, microns,
  onTypeChange, onSave, onCancel, saving, loadingCategories,
}) => {
  const noTypePicked = !form.tape_type_id;

  // --- live figures ---
  const micron = microns.find(m => String(m.id) === String(form.micron_id));
  const gsm    = form.isNewMicron ? Number(form.newMicronGsm || 0) : Number(micron?.gsm || 0);

  const widthMm = Number(form.width_mm || 0);
  const meters  = Number(form.default_meters || 0);
  const core    = Number(form.core_length_meters || 0);

  // g/m = gsm × width in metres
  let gramsPerMeter = gsm > 0 && widthMm > 0 ? gsm * (widthMm / 1000) : 0;
  let rollWeight    = gramsPerMeter > 0 && meters > 0 ? (gramsPerMeter * meters) / 1000 : 0;

  // A weighed delivery beats a calculated figure, so an
  // override back-solves g/m from the real weight.
  const overrode = form.roll_weight_kg !== '' && Number(form.roll_weight_kg) > 0;
  if (overrode && meters > 0) {
    rollWeight    = Number(form.roll_weight_kg);
    gramsPerMeter = (rollWeight * 1000) / meters;
  }

  const expectedCores = meters > 0 && core > 0 ? Math.floor(meters / core) : null;
  const metersPerKg   = gramsPerMeter > 0 ? 1000 / gramsPerMeter : null;

  const showFigures = gramsPerMeter > 0 && meters > 0;

  return (
    <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Jumbo type">
          <select
            style={inputStyle}
            value={form.tape_type_id}
            onChange={e => onTypeChange(e.target.value)}
          >
            <option value="">Select…</option>
            {tapeTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>

        <ComboField
          label="Category"
          options={categories}
          value={form.category_id}
          newName={form.newCategory}
          isNew={form.isNewCategory}
          disabled={noTypePicked}
          placeholder="Type a new category"
          hint={noTypePicked ? 'Pick a jumbo type first' : (loadingCategories ? 'Loading…' : undefined)}
          onChange={({ value, newName, isNew }) => setForm({
            ...form, category_id: value, newCategory: newName, isNewCategory: isNew,
          })}
        />

        <ComboField
          label="Quality"
          options={qualities}
          value={form.quality_id}
          newName={form.newQuality}
          isNew={form.isNewQuality}
          placeholder="Type a new quality"
          onChange={({ value, newName, isNew }) => setForm({
            ...form, quality_id: value, newQuality: newName, isNewQuality: isNew,
          })}
        />

        <MicronField microns={microns} form={form} setForm={setForm} />

        <Field
          label="Jumbo width (mm)"
          hint={widthMm > 0 ? `${mm2in(widthMm)}"` : undefined}
        >
          <TextInput
            type="number" step="0.01"
            value={form.width_mm}
            onChange={e => setForm({ ...form, width_mm: e.target.value })}
            placeholder="1280"
          />
        </Field>

        <Field
          label="Core width (mm)"
          hint={
            Number(form.core_width_mm) > 0
              ? `${mm2in(form.core_width_mm)}" — after edge trim`
              : 'Narrower than the jumbo, after edge trim'
          }
        >
          <TextInput
            type="number" step="0.01"
            value={form.core_width_mm}
            onChange={e => setForm({ ...form, core_width_mm: e.target.value })}
            placeholder="1220"
          />
        </Field>

        <Field label="Total meters">
          <TextInput
            type="number" step="0.01"
            value={form.default_meters}
            onChange={e => setForm({ ...form, default_meters: e.target.value })}
            placeholder="4000"
          />
        </Field>

        <Field label="Core length (meters)">
          <TextInput
            type="number" step="0.01"
            value={form.core_length_meters}
            onChange={e => setForm({ ...form, core_length_meters: e.target.value })}
            placeholder="68"
          />
        </Field>

        <Field
          label="Roll weight (kg)"
          hint="Leave blank to use the calculated weight"
        >
          <TextInput
            type="number" step="0.01"
            value={form.roll_weight_kg}
            onChange={e => setForm({ ...form, roll_weight_kg: e.target.value })}
            placeholder={rollWeight > 0 ? rollWeight.toFixed(1) : '0'}
          />
        </Field>

        {form.id && (
          <Field label="Status">
            <div style={{ paddingTop: '9px' }}>
              <Toggle
                checked={form.is_active}
                onChange={v => setForm({ ...form, is_active: v })}
                label={form.is_active ? 'Active' : 'Inactive'}
              />
            </div>
          </Field>
        )}
      </div>

      {/* Live figures — a typo in any field shows up here first */}
      {showFigures && (
        <div style={{
          background: '#ffffff', border: `1px solid ${C.border}`,
          borderRadius: '8px', padding: '16px 18px', marginBottom: '18px',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '16px',
          }}>
            {[
              ['Weight per meter', `${gramsPerMeter.toFixed(1)} g/m`],
              ['Roll weight',      `${rollWeight.toFixed(1)} kg${overrode ? ' (entered)' : ''}`],
              ['Expected cores',   expectedCores ?? '—'],
              ['1 kg of scrap',    metersPerKg ? `${metersPerKg.toFixed(1)} m` : '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: C.text, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '14px', paddingTop: '12px',
            borderTop: `1px solid ${C.borderSoft}`,
            fontSize: '12px', color: C.textMuted, lineHeight: 1.6,
          }}>
            When the operator reports scrap in kg, it is converted at
            {' '}<strong style={{ color: C.text }}>{gramsPerMeter.toFixed(1)} g/m</strong>{' '}
            and those meters come off the roll alongside the cores.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Save jumbo'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// E. MAIN COMPONENT
// ============================================================

const SettingsTab = () => {
  const [rows, setRows]             = useState([]);
  const [tapeTypes, setTapeTypes]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [qualities, setQualities]   = useState([]);
  const [microns, setMicrons]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingCats, setLoadingCats] = useState(false);
  const [form, setForm]             = useState(null);
  const [saving, setSaving]         = useState(false);
  const [banner, setBanner]         = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [j, t, q, m] = await Promise.all([
        axios.get(`${API}/tape/settings/jumbos`,     { headers }),
        axios.get(`${API}/tape/settings/tape-types`, { headers }),
        axios.get(`${API}/tape/settings/qualities`,  { headers }),
        axios.get(`${API}/tape/settings/microns`,    { headers }),
      ]);
      setRows(j.data.data || []);
      setTapeTypes(t.data.data || []);
      setQualities((q.data.data || []).filter(x => x.is_active));
      setMicrons((m.data.data || []).filter(x => x.is_active));
    } catch {
      notify('error', 'Could not load jumbo list. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const loadCategoriesForType = async (tapeTypeId) => {
    if (!tapeTypeId) { setCategories([]); return; }
    setLoadingCats(true);
    try {
      const res = await axios.get(
        `${API}/tape/settings/categories?tape_type_id=${tapeTypeId}`, { headers }
      );
      setCategories((res.data.data || []).filter(x => x.is_active));
    } catch {
      setCategories([]);
    } finally {
      setLoadingCats(false);
    }
  };

  // Changing the type clears the category — the previous pick
  // belongs to a different type and would be invalid.
  const handleTypeChange = (tapeTypeId) => {
    setForm(f => ({
      ...f,
      tape_type_id: tapeTypeId,
      category_id: '', newCategory: '', isNewCategory: false,
    }));
    loadCategoriesForType(tapeTypeId);
  };

  const openAdd = () => {
    setForm(emptyForm);
    setCategories([]);
  };

  const openEdit = (row) => {
    setForm({
      ...emptyForm,
      id: row.id,
      tape_type_id: String(row.tape_type_id),
      category_id: String(row.category_id),
      quality_id: String(row.quality_id),
      micron_id: row.micron_id ? String(row.micron_id) : '',
      width_mm: row.width_mm,
      core_width_mm: row.core_width_mm,
      default_meters: row.default_meters,
      core_length_meters: row.core_length_meters,
      roll_weight_kg: row.roll_weight_kg || '',
      is_active: row.is_active,
    });
    loadCategoriesForType(row.tape_type_id);
  };

  const save = async () => {
    const f = form;

    if (!f.tape_type_id) return notify('error', 'Pick a jumbo type');
    if (f.isNewCategory ? !f.newCategory.trim() : !f.category_id)
      return notify('error', 'Pick or add a category');
    if (f.isNewQuality ? !f.newQuality.trim() : !f.quality_id)
      return notify('error', 'Pick or add a quality');

    if (f.isNewMicron) {
      if (!(Number(f.newMicron) > 0))    return notify('error', 'Enter the micron value');
      if (!(Number(f.newMicronGsm) > 0)) return notify('error', 'Enter the weight per square metre');
    } else if (!f.micron_id) {
      return notify('error', 'Pick or add a micron');
    }

    if (!(Number(f.width_mm) > 0))           return notify('error', 'Width must be more than 0');
    if (!(Number(f.core_width_mm) > 0))      return notify('error', 'Core width must be more than 0');
    if (!(Number(f.default_meters) > 0))     return notify('error', 'Total meters must be more than 0');
    if (!(Number(f.core_length_meters) > 0)) return notify('error', 'Core length must be more than 0');

    if (Number(f.core_width_mm) > Number(f.width_mm))
      return notify('error', 'Core width cannot be wider than the jumbo');
    if (Number(f.core_length_meters) > Number(f.default_meters))
      return notify('error', 'Core length cannot be longer than the jumbo');

    setSaving(true);
    try {
      // New micron, category and quality get created first so
      // the jumbo has real ids to reference.
      let micronId = f.micron_id;
      if (f.isNewMicron) {
        const res = await axios.post(`${API}/tape/settings/microns`, {
          micron: f.newMicron, gsm: f.newMicronGsm,
        }, { headers });
        micronId = res.data.data.id;
      }

      let categoryId = f.category_id;
      if (f.isNewCategory) {
        const res = await axios.post(`${API}/tape/settings/categories`, {
          name: f.newCategory.trim(), tape_type_id: f.tape_type_id,
        }, { headers });
        categoryId = res.data.data.id;
      }

      let qualityId = f.quality_id;
      if (f.isNewQuality) {
        const res = await axios.post(`${API}/tape/settings/qualities`, {
          name: f.newQuality.trim(),
        }, { headers });
        qualityId = res.data.data.id;
      }

      const payload = {
        tape_type_id:       f.tape_type_id,
        category_id:        categoryId,
        quality_id:         qualityId,
        micron_id:          micronId,
        width_mm:           f.width_mm,
        core_width_mm:      f.core_width_mm,
        default_meters:     f.default_meters,
        core_length_meters: f.core_length_meters,
        roll_weight_kg:     f.roll_weight_kg,
        is_active:          f.is_active,
      };

      if (f.id) {
        await axios.put(`${API}/tape/settings/jumbos/${f.id}`, payload, { headers });
        notify('success', 'Jumbo updated');
      } else {
        await axios.post(`${API}/tape/settings/jumbos`, payload, { headers });
        notify('success', 'Jumbo added');
      }

      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/tape/settings/jumbos/${row.id}`, { headers });
      notify('success', 'Jumbo deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Jumbo
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Inward picks from this list. Micron and width set the weight used to
              turn reported scrap into meters.
            </p>
          </div>
          {!form && (
            <Btn onClick={openAdd}><Plus size={16} /> Add jumbo</Btn>
          )}
        </div>

        {form && (
          <JumboForm
            form={form}
            setForm={setForm}
            tapeTypes={tapeTypes}
            categories={categories}
            qualities={qualities}
            microns={microns}
            onTypeChange={handleTypeChange}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
            loadingCategories={loadingCats}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Jumbo</th>
                <th style={thStyle}>Micron</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Width</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Core width</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Meters</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>g/m</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cores</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rows.map(row => {
                const noWeight = !row.grams_per_meter || Number(row.grams_per_meter) <= 0;
                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '600', fontSize: '13px' }}>
                      {row.tape_type_name}
                      <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '400' }}>
                        {row.category_name} · {row.quality_name}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {row.micron ? `${Number(row.micron)} µm` : (
                        <span style={{ color: C.danger, fontSize: '12px' }}>not set</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {Number(row.width_mm)} mm
                      <div style={{ fontSize: '11px', color: C.textMuted }}>{mm2in(row.width_mm)}"</div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {Number(row.core_width_mm)} mm
                      <div style={{ fontSize: '11px', color: C.textMuted }}>{mm2in(row.core_width_mm)}"</div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {Number(row.default_meters).toLocaleString()} m
                      <div style={{ fontSize: '11px', color: C.textMuted }}>
                        core {Number(row.core_length_meters)} m
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {row.roll_weight_kg ? `${Number(row.roll_weight_kg)} kg` : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {noWeight ? (
                        <span style={{ color: C.danger, fontSize: '12px', fontWeight: '600' }}>
                          not set
                        </span>
                      ) : (
                        <>
                          {Number(row.grams_per_meter).toFixed(1)}
                          <div style={{ fontSize: '11px', color: C.textMuted }}>
                            1 kg = {Number(row.meters_per_kg).toFixed(0)} m
                          </div>
                        </>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                      {row.expected_cores}
                    </td>
                    <td style={tdStyle}><StatusPill active={row.is_active} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn title="Edit" onClick={() => openEdit(row)}>
                          <Pencil size={15} />
                        </IconBtn>
                        <IconBtn danger title="Delete" onClick={() => remove(row)}>
                          <Trash2 size={15} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="10" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No jumbos defined yet. Click "Add jumbo" to create the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;